import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAddressBook, IconMapPins } from '@tabler/icons-react';
import { Button, FormInstance } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { CrudMode, FormItemValueType } from '@ohpen/enso-ui';
import { IntermediaryResponse } from '@pmp/api';

import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { formatAddressAsLines } from '../../firms';
import { SchemaFormCheckbox } from '@pmp/utils';

export const ContactDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const [isEditing, setIsEditing] = useState(false);

  const addressTitle = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {t('lender.firms.labels.registeredOfficeAddress')}
        </span>
        <div>
          {!readonly && (
            <Button type="link" icon={isEditing ? <CloseOutlined /> : <EditOutlined />} onClick={() => setIsEditing(!isEditing)} />
          )}
        </div>
      </div>
    ),
    [readonly, t, isEditing],
  );

  const contactColumns: ProFormColumnsType<IntermediaryResponse>[] = useMemo(
    () => [
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.mobile'),
            dataIndex: ['personalData', 'mobilePhoneNumber'],
            valueType: FormItemValueType.text,
            readonly,
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.telephone'),
            dataIndex: ['personalData', 'workPhoneNumber'],
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.brokers.labels.email'),
        dataIndex: 'email',
        valueType: FormItemValueType.text,
        readonly,
        formItemProps: {
          rules: [{ required: true, type: 'email' }],
        },
        colProps: { span: 12 },
      },
      // TODO: copyCaseNotification field not available in new API
      {
        dataIndex: 'receiveMarketingMaterials',
        valueType: 'text' as const,
        readonly,
        renderFormItem: () => <SchemaFormCheckbox name="receiveMarketingMaterials" label={t('lender.brokers.labels.marketingMaterials')} />,
        render: () => <SchemaFormCheckbox name="receiveMarketingMaterials" label={t('lender.brokers.labels.marketingMaterials')} />,
        colProps: { span: 12 },
      },
      {
        dataIndex: 'agreedToTermsAndConditions',
        valueType: 'text' as const,
        readonly,
        renderFormItem: () => <SchemaFormCheckbox name="agreedToTermsAndConditions" label={t('lender.brokers.labels.termsAndConditions')} />,
        render: () => <SchemaFormCheckbox name="agreedToTermsAndConditions" label={t('lender.brokers.labels.termsAndConditions')} />,
        colProps: { span: 12 },
      },
    ],
    [readonly, t],
  );

  const addressDetailColumns: ProFormColumnsType<any, 'text'>[] = useMemo(
    () => [
      {
        valueType: 'group',
        title: addressTitle,
        columns: [],
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.addressLine1'),
            dataIndex: ['personalData', 'homeAddress', 'lineItems', 0],
            valueType: 'text',
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine1Placeholder'),
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine2'),
            dataIndex: ['personalData', 'homeAddress', 'lineItems', 1],
            valueType: 'text',
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.addressLine3'),
            dataIndex: ['personalData', 'homeAddress', 'lineItems', 2],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine4'),
            dataIndex: ['personalData', 'homeAddress', 'lineItems', 3],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.town'),
            dataIndex: ['personalData', 'homeAddress', 'city'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.county'),
            dataIndex: ['personalData', 'homeAddress', 'lineItems', 4],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.postCode'),
            dataIndex: ['personalData', 'homeAddress', 'postCode'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.country'),
            dataIndex: ['personalData', 'homeAddress', 'country'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, readonly],
  );

  const tradingAddressesColumns: ProFormColumnsType<IntermediaryResponse>[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.tradingName'),
        dataIndex: ['tradingAddress', 'name'],
        valueType: FormItemValueType.text,
        readonly,
        colProps: { span: 12 },
      },
      {
        title: t('lender.brokers.labels.tradingAddress'),
        dataIndex: ['tradingAddress', 'numberOrBuilding'],
        valueType: FormItemValueType.text,
        readonly,
        colProps: { span: 12 },
      },
      ...(!isEditing
        ? [
          {
            title: addressTitle,
            valueType: 'group' as const,
            columns: [],
            colProps: { span: 12 },
          },
          {
            dataIndex: ['personalData', 'homeAddress'],
            valueType: 'textarea' as const,
            fieldProps: (form: FormInstance) => {
              const addr = form?.getFieldValue(['personalData', 'homeAddress']);
              return {
                value: formatAddressAsLines(addr),
                autoSize: { minRows: 2 },
                disabled: true,
              };
            },
            colProps: { span: 12 },
          },
        ]
        : addressDetailColumns),
    ],
    [readonly, t, addressDetailColumns, isEditing, addressTitle],
  );

  return (
    <>
      <CrudFormCard icon={<IconAddressBook />} title={t('lender.brokers.labels.contactDetails')}>
        <BetaSchemaForm<IntermediaryResponse> form={form} layoutType="Embed" columns={contactColumns} submitter={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconMapPins />} title={t('lender.brokers.labels.tradingAddress')}>
        <BetaSchemaForm<IntermediaryResponse> form={form} layoutType="Embed" columns={tradingAddressesColumns} submitter={false} />
      </CrudFormCard>
    </>
  );
};
